import React, { useState, useRef, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Button } from 'modules/styled/web';
import { alert, text } from 'app/utils';
import { colors } from 'app/styles';
import UAvatar from 'modules/user/UAvatar.component';
import { Box, Spacer, View } from 'modules/styled';
import { createComment, updateComment } from 'modules/comment/comment.actions';
import history from 'modules/navigation/history';
import TextAreaWithMention from 'modules/text/web/textAreaWithMention';
import { useCommunityAuth } from 'modules/createPost/hooks';
import { RateLimitWarning } from '../ratelimitWarning';
import { ImageUpload } from 'modules/createPost/image/ImageUpload.web';

CommentFormComponent.propTypes = {
  edit: PropTypes.bool,
  comment: PropTypes.object,
  cancel: PropTypes.func,
  buttonText: PropTypes.string,
  parentPost: PropTypes.object,
  parentComment: PropTypes.object,
  nestingLevel: PropTypes.number,
  additionalNesting: PropTypes.number,
  autoFocus: PropTypes.bool,
  screenSize: PropTypes.number,
};

export function CommentFormComponent({
  comment,
  edit,
  cancel,
  nestingLevel,
  additionalNesting,
  autoFocus,
  parentComment,
  parentPost,
  buttonText,
  sx,
}) {
  const auth = useSelector((state) => state.auth);
  const screenSize = useSelector((state) => state.navigation.screenSize);

  const [focused, setFocused] = useState(false);
  const dispatch = useDispatch();

  const initalText = edit ? comment.body : '';
  const [commentText, setCommentText] = useState(initalText);

  const textArea = useRef();

  const handleChange = (e) => setCommentText(e.target.value);
  const addImage = (image) => setCommentText(commentText + image);

  const authError = useCommunityAuth();

  // TODO - non-shift enter?
  // handleKeydown(e) {
  //  if (e.keyCode == 13 && e.shiftKey == false) {
  //  }
  // }

  async function _createComment() {
    if (!auth.isAuthenticated) {
      return alert.browserAlerts.alert('Please log in to post comments');
    }
    if (!commentText || !commentText.length) {
      return alert.browserAlerts.alert('no comment');
    }

    const { mentions, tags } = getMentionsAndTags();

    const commentObj = {
      parentPost: parentPost._id,
      parentComment: parentComment ? parentComment._id : null,
      linkParent: parentPost.type === 'link' ? parentPost._id : null,
      text: commentText.trim(),
      tags,
      mentions,
      metaPost: parentPost.metaPost,
    };

    setCommentText('');
    const newComment = await dispatch(createComment(commentObj));
    if (!newComment) {
      setCommentText(commentText);
      if (textArea.current) textArea.current.focus();
      return null;
    }
    _cancel();
    return history.push(
      `/${newComment.community}/post/${newComment.parentPost}/${newComment._id}`
    );
  }

  async function _updateComment() {
    if (comment.body === commentText) {
      return _cancel();
    }
    const { mentions } = getMentionsAndTags();
    const originalText = comment.body;
    comment.body = commentText;
    comment.mentions = mentions;

    const newComment = await dispatch(updateComment(comment));
    if (newComment) return _cancel();

    comment.body = originalText;
    alert.browserAlerts.alert('Error updating comment');
    return null;
  }

  function getMentionsAndTags() {
    const words = text.getWords(commentText);
    const tags = text.getTags(words);
    const mentions = text.getMentions(words);
    return { mentions, tags };
  }

  function _cancel(e) {
    e && e.preventDefault();
    setCommentText('');
    textArea?.current?.blur();
    setFocused(false);
    cancel && cancel();
  }

  const handleSubmit = () => {
    // e.preventDefault();
    if (edit) return _updateComment();
    return _createComment();
  };

  if (!auth.isAuthenticated) return null;

  const backgroundColor = !edit && focused ? colors.secondaryBG : 'transparent';
  const paddingTop = !edit && focused ? 4 : 0;

  const postType = parentComment ? 'comment' : 'post';

  return (
    <Spacer
      sx={{ flexDirection: 'row', ...sx, pt: paddingTop, bg: backgroundColor }}
      nestingLevel={screenSize ? 0 : nestingLevel}
      additionalNesting={screenSize ? 0 : additionalNesting}
      screenSize={screenSize}
    >
      {authError && focused ? (
        authError.component
      ) : (
        <View sx={{ flexDirection: 'column', flex: 1, position: 'relative' }}>
          {focused && !edit && (
            <Box sx={{ mb: 2 }}>
              <RateLimitWarning postType={postType} />
            </Box>
          )}
          <TextAreaWithMention
            textArea={textArea}
            value={commentText}
            autoFocus={autoFocus}
            leftPadding={focused ? 2 : 6}
            onChange={handleChange}
            setFocused={setFocused}
            ImageUpload={
              focused ? () => <ImageUpload insertImages={addImage} /> : () => null
            }
            // withPreview
            placeholder={'Enter comment...'}
            focused={focused}
            minheight={focused ? 9 : 0}
            sx={{ flex: 1 }}
          >
            {focused ? null : (
              <View
                sx={{ flex: 1, p: 2, position: 'absolute', left: 0, top: 0, zIndex: 10 }}
              >
                <UAvatar user={auth.user} size={4} noLink />
              </View>
            )}
          </TextAreaWithMention>
          {focused || commentText ? (
            <View sx={{ justifyContent: 'flex-end', flexDirection: 'row' }}>
              <Button
                onMouseDown={_cancel}
                onTouchStart={_cancel}
                sx={{
                  bg: 'transparent',
                  color: 'grey',
                  px: 4,
                  minWidth: 0,
                  mr: 1,
                }}
                // textSx={{ color: 'white' }}
                disabled={!auth.isAuthenticated}
              >
                Cancel
              </Button>
              <Button
                onMouseDown={handleSubmit}
                onTouchStart={handleSubmit}
                disabled={!auth.isAuthenticated}
                sx={{ px: 4, minWidth: 0 }}
              >
                {buttonText}
              </Button>
            </View>
          ) : null}
        </View>
      )}
    </Spacer>
  );
}

export default memo(CommentFormComponent);
